import { Fragment, useContext, useEffect, useState, useRef } from "react";
import { SessionContext } from "../../../utility/useSession";
import flex from "../../../styles/flex";

import NotFoundPage from "../../error/NotFoundPage";
import { useDate } from "../../../utility/useDate";
import DateField from "../../../components/editable/EditableField/fields/DateField";
import { FiCheckSquare, FiDownload, FiShare, FiSquare } from "react-icons/fi";
import DropDownField from "../../../components/editable/EditableField/fields/DropDownField";
import { Bus, IBusDriver, IWalkaroundReport, IWalkaroundReportList, api } from "../../../logic/api";
import { PagedDataView } from "../../../components/lists/PagedDataView/PagedDataView";
import { IconButton } from "@mui/material";
import WaReportDownloader from "./WaReportDownloader";
import { useTranslation } from "react-i18next";

const defaultColumnWidth = 140;
const countersColumnWidth = 100;

export const WaReports = () => {
    const session = useContext(SessionContext);
    const { t } = useTranslation();

    if (session?.current?.site?.preferences.bus_walkaround_enabled != true) {
        return <NotFoundPage title={t("notFoundPage.notAvailable")} message={t("notFoundPage.featureNotEnabled")} />
    }

    const getBusList = api.hooks.bus.listUnpaginated();
    const getBusDriversList = api.hooks.bus.listDriversUnpaginated();

    const { localizeDate } = useDate();

    const startDateDefault = new Date();
    startDateDefault.setHours(0);
    startDateDefault.setMinutes(0);
    startDateDefault.setSeconds(0);
    startDateDefault.setMilliseconds(0);

    const endDateDefault = new Date();
    endDateDefault.setHours(23);
    endDateDefault.setMinutes(59);
    endDateDefault.setSeconds(59);
    endDateDefault.setMilliseconds(999);

    const [startDate, setStartDate] = useState(localizeDate(startDateDefault).toISO() ?? "");
    const [endDate, setEndDate] = useState(localizeDate(endDateDefault).toISO() ?? "");
    const [selectedReportIds, setSelectedReportIds] = useState<number[]>([-1]);
    const [selectedBus, setSelectedBus] = useState<Bus | null>({
        reg: "",
        id: 0,
        active: true,
        isVOR: false,
        distanceUnit: "miles",
    });
    const [selectedDriver, setSelectedDriver] = useState<IBusDriver>({
        id: 0,
        name: "",
    });
    const [listReportParams, setListReportParams] = useState<IWalkaroundReportList>({
        busId: selectedBus?.id ?? 0,
        busDriverId: selectedDriver?.id ?? 0,
        startDate: startDate,
        endDate: endDate,
        idsOnly: false,
    });

    //Does the list contain a -1?
    const allReportsSelected = selectedReportIds.includes(-1);

    //get the height of the viewport
    const [height, setHeight] = useState(window.innerHeight);
    const [width, setWidth] = useState(window.innerWidth);
    useEffect(() => {
        const handleResize = () => {
            setHeight(window.innerHeight);
            setWidth(window.innerWidth);
        };
        window.addEventListener('resize', handleResize);
        return () => window.removeEventListener('resize', handleResize);
    }, []);

    const tableStartRef = useRef<HTMLDivElement>(null);
    const rowLayout = width < 1200 ? {} : flex.flexRows;
    const columnLayout = width < 1200 ? { display: "inline-block", paddingBottom: 10, width: 250, } : flex.flexAuto;
    const columnSpace = width < 1200 ? { display: "none" } : { ...flex.flexAuto, width: 20 };
    const tableHeight = height - (tableStartRef.current?.getBoundingClientRect().top ?? 0) - 70;

    const [showDownloader, setShowDownloader] = useState(false);

    const _downloadSelectedFiles = () => {
        setShowDownloader(true);
    }

    const onReportSelected = (id: number) => {
        if (selectedReportIds.includes(id)) {
            setSelectedReportIds(selectedReportIds.filter((id) => id != id));
        } else {
            setSelectedReportIds([...selectedReportIds, id]);
        }
    }



    return (
        <div style={{ width: "100%", maxWidth: 2080, display: "inline-block", textAlign: "left" }}>

            {width >= 1200 && (
                <Fragment>
                    <div style={flex.flexRows}>
                        <div style={flex.flexFill}>
                            <h2 style={{ marginTop: 0 }}>{t("walkarounds.busReports")}</h2>
                        </div>
                    </div>
                    <div style={{ height: 10 }}></div>
                </Fragment>
            )}

            <div style={rowLayout}>
                <div style={columnLayout}>
                    <DateField label={t("booking.labels.startDate")} value={startDate} onChange={(date) => {
                        setStartDate(date);
                        setSelectedReportIds([-1]);
                        setListReportParams({
                            ...listReportParams,
                            startDate: date,
                        });
                    }}></DateField>
                </div>
                <div style={columnSpace}></div>
                <div style={columnLayout}>
                    <DateField label={t("booking.labels.endDate")} value={endDate} onChange={(date) => {
                        setEndDate(date);
                        setSelectedReportIds([-1]);
                        setListReportParams({
                            ...listReportParams,
                            endDate: date,
                        });
                        setSelectedReportIds([-1]);
                    }}></DateField>
                </div>
                <div style={columnSpace}></div>
                <div style={columnLayout}>
                    <DropDownField
                        minWidth={180}
                        readOnly={false}
                        label={t("walkarounds.bus")}
                        selectedItem={
                            {
                                name: selectedBus?.reg ?? "",
                                value: `${selectedBus?.id ?? ""}`,
                            }
                        }
                        onRequestData={async () => {
                            return new Promise((accept) => {
                                getBusList.send(session.current.context, {}, (b) => {
                                    const buses = (b ?? []) as Bus[];
                                    accept([{
                                        name: t("walkarounds.allBuses"),
                                        value: "0",
                                    }, ...buses.map((bus) => {
                                        return {
                                            name: bus.reg,
                                            value: `${bus.id}`,
                                        }
                                    }),]);
                                });
                            });
                        }}
                        onChange={(value) => {
                            setSelectedReportIds([-1]);
                            setSelectedBus({
                                id: parseInt(value.value ?? ""),
                                reg: value.name,
                                active: true,
                                isVOR: false,
                                distanceUnit: "miles",
                            });
                            setListReportParams({
                                ...listReportParams,
                                busId: parseInt(value.value ?? ""),
                            });
                            setSelectedReportIds([-1]);
                        }}
                    ></DropDownField>
                </div>
                <div style={columnSpace}></div>
                <div style={columnLayout}>
                    <DropDownField
                        minWidth={180}
                        readOnly={false}
                        label={t("walkarounds.driver")}
                        selectedItem={
                            {
                                name: selectedDriver?.name ?? "",
                                value: `${selectedDriver?.id ?? ""}`,
                            }
                        }
                        onRequestData={async () => {
                            return new Promise((accept) => {
                                getBusDriversList.send(session.current.context, {}, (b) => {
                                    const drivers = (b ?? []) as IBusDriver[];
                                    accept([{
                                        name: t("walkarounds.allDrivers"),
                                        value: "0",
                                    }, ...drivers.map((bus) => {
                                        return {
                                            name: bus.name,
                                            value: `${bus.id}`,
                                        }
                                    }),]);
                                });
                            });
                        }}
                        onChange={(value) => {
                            setSelectedReportIds([-1]);
                            setSelectedDriver({
                                id: parseInt(value.value ?? ""),
                                name: value.name,
                            });
                            setListReportParams({
                                ...listReportParams,
                                busDriverId: parseInt(value.value ?? ""),
                            });
                            setSelectedReportIds([-1]);
                        }}
                    ></DropDownField>

                </div>

                <div style={flex.flexFill}></div>
                <div style={columnSpace}></div>
                <div style={columnLayout}>
                    <WaReportDownloader reportParams={listReportParams} show={showDownloader} walkaroundResultIds={selectedReportIds} callBack={() => {
                        setShowDownloader(false);
                    }} >
                        <a href="#" onClick={_downloadSelectedFiles} style={{ fontSize: 18, fontWeight: "bold", color: "black" }}><FiDownload /><span style={{ paddingLeft: 5 }}>
                            {t("walkarounds.downloadPdfs")}
                        </span></a>
                    </WaReportDownloader>
                </div>
            </div>

            <div style={{ height: 25 }}></div>
            <div ref={tableStartRef}></div>
            <div style={{ width: "100%", height: tableHeight, overflow: "auto", border: "solid 1px #eee" }}>
                <PagedDataView<IWalkaroundReportList, IWalkaroundReport>
                    apiHook={api.hooks.walkaroundReport.list}

                    parameters={listReportParams}
                    renderItem={(item) => {
                        return <WaReportItem listReportParams={listReportParams} key={item.id} item={item} allSelected={allReportsSelected} selected={selectedReportIds.includes(item.id)} onSelected={() => onReportSelected(item.id)} />
                    }}
                    header={(snapshot, records) => {
                        if (records.length == 0) return <div></div>;
                        const allCounters = records.map((r) => r.counters);
                        const uniqueCounterNames = ([...new Set(allCounters.flat().map((c => c.alias)))]);
                        const columnStyle = { padding: 20, height: 42, fontWeight: "bold", backgroundColor: "white", overflow: "hidden", textOverflow: "ellipsis", };
                        return (
                            <div style={{ ...flex.flexRows, position: "sticky", top: 0, zIndex: 100 }}>

                                <div style={{ ...flex.flexAuto, width: defaultColumnWidth }}>
                                    <div style={{ ...columnStyle, textAlign: "center" }}>{t("walkarounds.date")}</div>
                                </div>
                                <div style={{ ...flex.flexAuto, width: defaultColumnWidth }}>
                                    <div style={{ ...columnStyle, textAlign: "center" }}>{t("walkarounds.bus")}</div>
                                </div>
                                <div style={{ ...flex.flexAuto, width: defaultColumnWidth }}>
                                    <div style={{ ...columnStyle, textAlign: "center" }}>{t("walkarounds.driver")}</div>
                                </div>
                                <div style={{ ...flex.flexAuto, width: defaultColumnWidth }}>
                                    <div style={{ ...columnStyle, textAlign: "center" }}>{t("walkarounds.mileage")}</div>
                                </div>
                                <div style={{ ...flex.flexAuto, width: defaultColumnWidth }}>
                                    <div style={{ ...columnStyle, textAlign: "center" }}>{t("walkarounds.duration")}</div>
                                </div>
                                {
                                    (uniqueCounterNames ?? []).map((name, i) => {
                                        return <div key={i} style={{ ...flex.flexAuto, width: countersColumnWidth }}>
                                            <div style={{ ...columnStyle, textAlign: "center" }}>{name}</div>
                                        </div>
                                    })
                                }
                                <div style={{ ...flex.flexAuto, position: "sticky", top: 0, right: 0, zIndex: 100, width: 50, backgroundColor: "white", }}>

                                    <div style={{ marginTop: 20 }}>
                                        <IconButton
                                            style={{
                                                height: 40,
                                                width: 40,
                                                padding: 8,
                                            }}
                                            onClick={() => {
                                                if (allReportsSelected) {
                                                    setSelectedReportIds([]);
                                                } else {
                                                    setSelectedReportIds([-1]);
                                                }
                                            }}>
                                            {allReportsSelected ? <FiCheckSquare /> : <FiSquare />}
                                        </IconButton>
                                    </div>
                                </div>
                            </div>
                        );
                    }}
                >
                    <div style={{ padding: 20, backgroundColor: "#eee" }}>
                        {t("walkarounds.noReports")}
                    </div>
                </PagedDataView>
            </div>
        </div>
    );
}

interface IWaReportItem {
    item: IWalkaroundReport;
    listReportParams: IWalkaroundReportList
    selected: boolean;
    allSelected: boolean;
    onSelected: () => void;
}
const WaReportItem = (props: IWaReportItem) => {

    const blueWithAlpha = "rgba(0, 100, 255, 0.05)";
    const redWithAlpha = "rgba(255, 0, 0, 0.07)";
    const rowColor = props.item.hasVOR ? redWithAlpha : blueWithAlpha;
    const columnStyle = { paddingLeft: 20, paddingRight: 20, paddingTop: 15, paddingBottom: 10, height: 25, backgroundColor: rowColor, overflow: "hidden", textOverflow: "ellipsis", };
    const dt = useDate();
    const [showDownload, setShowDownloader] = useState(false);

    return (
        <div style={{ backgroundColor: "white", marginTop: 10 }}>
            <div style={flex.flexRows}>

                <div style={{ ...flex.flexAuto, width: defaultColumnWidth }}>
                    <a href="#" style={{ color: "black", }}>
                        <div style={{ ...columnStyle, textAlign: "center" }}>

                            <WaReportDownloader walkaroundResultIds={
                                [props.item.id]
                            }
                                show={showDownload}
                                reportParams={props.listReportParams}
                                callBack={() => {
                                    setShowDownloader(false);
                                }}
                            >
                                <div style={flex.flexRows} onClick={() => setShowDownloader(true)}>
                                    <div style={flex.flexFill}></div>
                                    <div style={flex.flexAuto}>{dt.toFormattedFullDateTimeString(props.item.date)}</div>
                                    <div style={{ ...flex.flexAuto, width: 4 }}></div>
                                    <div style={flex.flexAuto}><FiShare size={18}></FiShare></div>

                                </div>
                            </WaReportDownloader>

                        </div>
                    </a>
                </div>
                <div style={{ ...flex.flexAuto, width: defaultColumnWidth }}>
                    <div style={{ ...columnStyle, textAlign: "center" }}>{props.item.bus ?? ""}</div>
                </div>
                <div style={{ ...flex.flexAuto, width: defaultColumnWidth }}>
                    <div style={{ ...columnStyle, textAlign: "center" }}>{props.item.driver ?? ""}</div>
                </div>
                <div style={{ ...flex.flexAuto, width: defaultColumnWidth }}>
                    <div style={{ ...columnStyle, textAlign: "center" }}>{props.item.mileage ?? ""}</div>
                </div>
                <div style={{ ...flex.flexAuto, width: defaultColumnWidth }}>
                    <div style={{ ...columnStyle, textAlign: "center" }}>{props.item.duration ?? ""}</div>
                </div>
                {
                    props.item.counters.map((counter, i) => {
                        return <div key={i} style={{ ...flex.flexAuto, width: countersColumnWidth }}>
                            <div style={{ ...columnStyle, textAlign: "center" }}>{counter.count}</div>
                        </div>
                    })
                }
                <div style={{ ...flex.flexAuto, width: 50, position: "sticky", right: 0, zIndex: 99, backgroundColor: "white" }}>
                    <div style={{ marginTop: 5 }}>

                        <IconButton onClick={() => props.onSelected()} style={{
                            height: 40,
                            width: 40,
                            padding: 8,
                        }}>
                            {((props.allSelected == false && props.selected == true) || (props.allSelected && props.selected == false)) ? <FiCheckSquare /> : <FiSquare />}
                        </IconButton>
                    </div>

                </div>
            </div>
        </div>
    );
};
